import { useEffect, useMemo, useRef, useState } from 'react';
import { api, runWithAuditReason } from '../../api/client';
import { FormTitle } from '../../components/FormTitle';
import { AuditTrailButton } from '../../components/AuditTrailButton';
import { LoaderSymbol } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate } from '../../components/confirm';
import { AppToolbar, type ExportData } from '../../components/AppToolbar';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../components/hotkeys';
import { DualListPickerDialog } from '../../components/DualListPicker';
import { ViewToggle } from '../../components/ViewToggle';
import { exportRows } from '../../utils/exportData';
import './security.css';

// ─────────────────────────────────────────────────────────────────────────
// User & Security → Roles  (ALPACA List → full-page Detail anatomy)
// ─────────────────────────────────────────────────────────────────────────
//   LIST (landing): single Name column in the shared <AppDataGrid>; toolbar =
//   Audit · New · Delete · Refresh · Export to.  Row click opens the record in
//   a FULL-PAGE detail (replaces the list — BO UI Standard §4, no popup):
//   Name* + the ALPACA "MAC ID" child grid (Link / Unlink via the shared
//   DualListPickerDialog) + the Intelligence per-nav-item permission grid and
//   Dashboards grants (kept — our rights model must not change).
//
//   Edits the LIVE RoleNavigationPermission templates + RoleNavigationPermission-
//   Details (the rows AI's AI_Auth_UserNavPermissions_SP reads to gate the nav).
//   Editing a role changes what EVERY user with that role sees, at once.
//   ALPACA regenerates its per-user CustomSystemRole security on its next save.
// ─────────────────────────────────────────────────────────────────────────

// ALPACA parity (2026-07-20, user decision): the Role DetailView never shows a
// per-screen permission grid — RoleNavigationPermissionDetails is a real table
// but its LayoutGroup is Removed="True" in legacy's Model.xafml, leaving only
// the "MAC ID" tab visible. UI-only hide — the underlying read/write plumbing
// (perms/savePerms/etc.) is untouched, so re-enabling is a one-line flip.
const SHOW_SCREEN_PERMISSIONS = false;
// Same rationale — legacy's RoleNavigationPermission BO has no dashboard-grant
// concept at all (confirmed: no VisibleInDashboards-adjacent property, no such
// tab in Model.xafml). AI-only addition; hidden for legacy parity (2026-07-20).
const SHOW_DASHBOARD_GRANTS = false;

function lcKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(lcKeys);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) out[k.toLowerCase()] = lcKeys(val);
    return out;
  }
  return v;
}
const errMsg = (e: unknown) =>
  (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
  ?? (e as { message?: string }).message ?? 'Request failed.';

type RoleItem = { oid: string; roleName: string; isAdministrative: boolean; sort: number; navCount: number; userCount: number };
type NavItem = { id: string; navigationId: string | null; caption: string | null; view: string | null; itempath: string | null; parent: string | null };
type Flags = { read: boolean; write: boolean; create: boolean; delete: boolean; navigate: boolean };
type PermMap = Record<string, Flags>;
const NO_FLAGS: Flags = { read: false, write: false, create: false, delete: false, navigate: false };
const FLAG_KEYS: (keyof Flags)[] = ['navigate', 'read', 'write', 'create', 'delete'];
const FLAG_LABEL: Record<keyof Flags, string> = { navigate: 'Nav', read: 'Read', write: 'Write', create: 'Create', delete: 'Delete' };
const anyFlag = (f: Flags) => f.read || f.write || f.create || f.delete || f.navigate;

// Enforce the permission hierarchy  Navigate ⊇ Read ⊇ {Write, Create, Delete}
// (ALPACA's intent — it forces Read+Nav on; we make it explicit & reversible).
//  • ticking Read           → Nav on
//  • ticking W / C / D      → Read + Nav on
//  • clearing Nav           → Read, Write, Create, Delete off
//  • clearing Read          → Write, Create, Delete off
function cascade(cur: Flags, k: keyof Flags, val: boolean): Flags {
  const next: Flags = { ...cur, [k]: val };
  if (val) {
    if (k === 'read') next.navigate = true;
    if (k === 'write' || k === 'create' || k === 'delete') { next.read = true; next.navigate = true; }
  } else {
    if (k === 'navigate') { next.read = false; next.write = false; next.create = false; next.delete = false; }
    if (k === 'read') { next.write = false; next.create = false; next.delete = false; }
  }
  return next;
}

// lcKeys lowercases every key; map that lowercase bag back into our typed shapes
// (reading camelCase straight off lcKeys output is what left the legacy list blank).
type Rec = Record<string, unknown>;
const arr = (data: unknown): Rec[] => (((lcKeys(data) as { data?: Rec[] }).data) ?? []);
const mapRoleItem = (r: Rec): RoleItem => ({
  oid: String(r.oid), roleName: String(r.rolename ?? ''), isAdministrative: !!r.isadministrative,
  sort: Number(r.sort ?? 0), navCount: Number(r.navcount ?? 0), userCount: Number(r.usercount ?? 0),
});
const mapNav = (n: Rec): NavItem => ({
  id: String(n.id), navigationId: (n.navigationid as string) ?? null, caption: (n.caption as string) ?? null,
  view: (n.view as string) ?? null, itempath: (n.itempath as string) ?? null, parent: (n.parent as string) ?? null,
});

type MacRef = { oid: string; macId: string; moduleName: string; description: string };

export default function RolesAdminPage() {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const confirm = useConfirm();

  const [roles, setRoles]     = useState<RoleItem[]>([]);
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [err, setErr]         = useState<string | null>(null);
  const [search, setSearch]   = useState('');
  const [selOid, setSelOid]   = useState<string | null>(null);
  // 'list' = landing grid.  Opening an EXISTING record shows the detail
  // READ-ONLY first ('view' — ALPACA parity, BO UI Standard v2 2026-07-02);
  // the detail's Edit button switches to 'edit'.  New goes straight to 'new'.
  const [mode, setMode]       = useState<'list' | 'new' | 'view' | 'edit'>('list');
  const [roleName, setRoleName] = useState('');
  // AdministrativePrivilege / Sort are NOT surfaced (user decision — same as
  // ALPACA's Role DetailView, which hides them).  The loaded values are kept in
  // state so an edit round-trips them unchanged.
  const [isAdmin, setIsAdmin]   = useState(false);
  const [sort, setSort]         = useState(0);
  const [saving, setSaving]     = useState(false);
  const [perms, setPerms]       = useState<PermMap>({});
  const [permSearch, setPermSearch] = useState('');
  const [savingPerms, setSavingPerms] = useState(false);
  // List selection (checkbox column) — drives the toolbar Delete.
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  // Dashboards & Backlog grants (AssignDashboardToUserDepartment, per role).
  const [dashboards, setDashboards] = useState<{ oid: string; title: string }[]>([]);
  const [roleDash, setRoleDash] = useState<Set<string>>(new Set());
  const [dashSearch, setDashSearch] = useState('');
  const [savingDash, setSavingDash] = useState(false);
  // MAC levels assigned to the role (ALPACA Role DetailView "MAC ID" child grid).
  const [allMacs, setAllMacs] = useState<MacRef[]>([]);
  const [roleMacs, setRoleMacs] = useState<Set<string>>(new Set());
  const [selMacRows, setSelMacRows] = useState<Set<string | number>>(new Set());
  const [macPickerOpen, setMacPickerOpen] = useState(false);

  const load = () => {
    setLoading(true); setErr(null);
    const t0 = performance.now();
    Promise.all([
      api.get('/api/v1/security/roles'),
      navItems.length === 0 ? api.get('/api/v1/security/roles/nav-items') : Promise.resolve(null),
      dashboards.length === 0 ? api.get('/api/v1/security/roles/dashboards') : Promise.resolve(null),
      allMacs.length === 0 ? api.get('/api/v1/security/roles/macs') : Promise.resolve(null),
    ]).then(([rr, nr, dr, mr]) => {
      setRoles(arr(rr.data).map(mapRoleItem));
      if (nr) setNavItems(arr(nr.data).map(mapNav));
      if (dr) setDashboards(arr(dr.data).map(d => ({ oid: String(d.oid).toLowerCase(), title: String(d.title ?? '') })));
      if (mr) setAllMacs(arr(mr.data).map(m => ({ oid: String(m.macoid).toLowerCase(), macId: String(m.macid ?? ''), moduleName: String(m.modulename ?? ''), description: String(m.description ?? '') })));
    }).catch(e => setErr(errMsg(e))).finally(() => { setLoadMs(performance.now() - t0); setLoading(false); });
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const t = search.trim().toLowerCase();
    return t ? roles.filter(r => r.roleName.toLowerCase().includes(t)) : roles;
  }, [roles, search]);

  // ALPACA-parity New flow: a slim detail with ONLY Name*; after the first save
  // it re-opens as the full detail (MAC ID + permissions sections).
  const openNew = () => { setMode('new'); setSelOid(null); setRoleName(''); setIsAdmin(false); setSort(0); setPerms({}); setPermSearch(''); setRoleMacs(new Set()); setSelMacRows(new Set()); };

  const openEdit = async (oid: string) => {
    setSelOid(oid); setPermSearch(''); setDashSearch(''); setSelMacRows(new Set());
    try {
      const [rr, pr, dr, mr] = await Promise.all([
        api.get(`/api/v1/security/roles/${oid}`),
        api.get(`/api/v1/security/roles/${oid}/permissions`),
        api.get(`/api/v1/security/roles/${oid}/dashboards`),
        api.get(`/api/v1/security/roles/${oid}/macs`),
      ]);
      const d = (lcKeys(rr.data) as { data?: { rolename: string; isadministrative: boolean; sort: number } }).data;
      const pRows = ((lcKeys(pr.data) as { data?: { navitemid: string; read: boolean; write: boolean; create: boolean; delete: boolean; navigate: boolean }[] }).data) ?? [];
      const map: PermMap = {};
      for (const p of pRows) map[p.navitemid.toLowerCase()] = { read: p.read, write: p.write, create: p.create, delete: p.delete, navigate: p.navigate };
      setPerms(map);
      const dRows = ((lcKeys(dr.data) as { data?: string[] }).data) ?? [];
      setRoleDash(new Set(dRows.map(x => String(x).toLowerCase())));
      const mRows = ((lcKeys(mr.data) as { data?: { macoid: string }[] }).data) ?? [];
      setRoleMacs(new Set(mRows.map(x => String(x.macoid).toLowerCase())));
      // Opening from the list (row click / pencil / prev-next / Detail tab)
      // always lands in read-only VIEW mode; Edit unlocks it.
      if (d) { setRoleName(d.rolename ?? ''); setIsAdmin(d.isadministrative); setSort(d.sort ?? 0); setMode('view'); }
    } catch (e) { notify.error(errMsg(e)); }
  };

  const closeDetail = () => { setMode('list'); setSelOid(null); };

  // Detail-view toolbar parity (matches the Module Access Control fix): New /
  // Delete / Refresh alongside Edit — not just Edit + Close.
  const refreshCurrent = () => {
    load();
    if (selOid) void openEdit(selOid);
  };
  const deleteCurrent = async () => {
    if (!selOid) return;
    const row = roles.find(r => r.oid === selOid);
    const label = row?.roleName ?? roleName;
    if (!(await confirm.confirm({ title: 'Delete role?', message: `Delete "${label}"? Users with this role will be unassigned. (soft-delete — recoverable)` }))) return;
    try {
      await api.delete(`/api/v1/security/roles/${selOid}`);
      notify.success('Role deleted.');
      closeDetail();
      load();
    } catch {
      notify.error('Deletion restricted. This data is linked to other records.');
    }
  };

  const toggleDash = (oid: string) => setRoleDash(s => { const n = new Set(s); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const shownDash = useMemo(() => {
    const t = dashSearch.trim().toLowerCase();
    return t ? dashboards.filter(d => d.title.toLowerCase().includes(t)) : dashboards;
  }, [dashboards, dashSearch]);

  const saveDash = () => {
    if (!selOid) return;
    setSavingDash(true);
    runSave({
      save: async () => { await api.put(`/api/v1/security/roles/${selOid}/dashboards`, { dashboardIds: [...roleDash] }); },
      successMessage: 'Dashboard access updated successfully.',
      onError: e => notify.error(errMsg(e)),
    }).finally(() => setSavingDash(false));
  };

  // ── MAC ID child grid (ALPACA Role DetailView) — Link / Unlink ────────────
  // Link opens the shared DualListPickerDialog; Unlink removes the selected
  // child-grid rows.  Both edit the local set; the detail's Save persists it
  // through the EXISTING role-MAC mutation (PUT /roles/{oid}/macs).
  const linkedMacs = useMemo(
    () => allMacs.filter(m => roleMacs.has(m.oid)),
    [allMacs, roleMacs]);
  const unlinkSelectedMacs = () => {
    if (selMacRows.size === 0) { notify.warning('Select at least one MAC row to unlink.'); return; }
    setRoleMacs(s => { const n = new Set(s); for (const id of selMacRows) n.delete(String(id)); return n; });
    setSelMacRows(new Set());
  };
  const exportMacsCsv = () =>
    void exportRows(linkedMacs, [
      { header: 'MACID', value: m => m.macId },
      { header: 'Description', value: m => m.description || '' },
    ], { filename: 'role-mac-levels' });

  const macCols: AppColumn<MacRef>[] = [
    { field: 'macId', header: 'MACID', width: 320 },
    { field: 'description', header: 'Description' },
  ];

  const saveRole = () => {
    if (!roleName.trim()) { notify.error('Role name is required.'); return; }
    setSaving(true);
    runSave({
      save: async () => {
        const bodyData = { roleName: roleName.trim(), isAdministrative: isAdmin, sort };
        if (mode === 'new') {
          const r = await api.post('/api/v1/security/roles', bodyData);
          const d = (lcKeys(r.data) as { data?: { oid: string } }).data;
          load(); if (d) await openEdit(d.oid);
        } else if (selOid) {
          await api.put(`/api/v1/security/roles/${selOid}`, bodyData);
          // ALPACA parity: the MAC ID child grid saves with the record's Save
          // (same EXISTING role-MAC mutation the old "Save MAC levels" used).
          await api.put(`/api/v1/security/roles/${selOid}/macs`, { macOids: [...roleMacs] });
          load();
        }
      },
      successMessage: mode === 'new' ? 'Role created successfully.' : 'Role saved successfully.',
      // ALPACA parity: after a successful save the detail returns to read-only
      // VIEW mode (a New role re-opens via openEdit, which already lands there).
      onSaved: () => { if (mode === 'edit') setMode('view'); },
      onError: e => notify.error(errMsg(e)),
    }).finally(() => setSaving(false));
  };

  // Toolbar Delete — operates on the list selection (same DELETE endpoint as
  // before).  ALPACA guard message shown verbatim when the API rejects.
  const delSelected = async () => {
    const targets = roles.filter(r => sel.has(r.oid));
    if (targets.length === 0) { notify.warning('Select at least one role to delete.'); return; }
    const names = targets.map(t => t.roleName).join(', ');
    if (!(await confirm.confirm({ title: 'Delete role?', message: `Delete "${names}"? Users with this role will be unassigned. (soft-delete — recoverable)` }))) return;
    try {
      for (const t of targets) await api.delete(`/api/v1/security/roles/${t.oid}`);
      notify.success('Role deleted.');
      setSel(new Set());
      if (selOid && targets.some(t => t.oid === selOid)) closeDetail();
      load();
    } catch {
      notify.error('Deletion restricted. This data is linked to other records.');
    }
  };

  // Full-format "Export to" (all 10 ALPACA formats) of the currently-filtered
  // roles list — same single Name column as the legacy CSV — via the shared
  // <ExportMenu> (AppToolbar exportData).
  const exportData: ExportData = {
    filename: 'roles',
    headers: ['Name'],
    rows: shown.map(r => [r.roleName]),
  };

  // ── nav-permission grid ───────────────────────────────────────────────────
  const flagsOf = (id: string): Flags => perms[id.toLowerCase()] ?? NO_FLAGS;
  const setFlag = (id: string, k: keyof Flags, val: boolean) =>
    setPerms(p => {
      const cur = p[id.toLowerCase()] ?? NO_FLAGS;
      return { ...p, [id.toLowerCase()]: cascade(cur, k, val) };
    });

  const filteredNav = useMemo(() => {
    const t = permSearch.trim().toLowerCase();
    return !t ? navItems
      : navItems.filter(n => (n.caption ?? '').toLowerCase().includes(t)
          || (n.parent ?? '').toLowerCase().includes(t)
          || (n.itempath ?? '').toLowerCase().includes(t)
          || (n.view ?? '').toLowerCase().includes(t));
  }, [navItems, permSearch]);

  const grantedCount = useMemo(() => Object.values(perms).filter(anyFlag).length, [perms]);

  const bulkSet = (k: keyof Flags, val: boolean) =>
    setPerms(p => {
      const next = { ...p };
      for (const n of filteredNav) {
        const id = n.id.toLowerCase();
        next[id] = cascade(next[id] ?? NO_FLAGS, k, val);
      }
      return next;
    });

  const savePerms = () => {
    if (!selOid) return;
    const payload = Object.entries(perms)
      .filter(([, f]) => anyFlag(f))
      .map(([navItemId, f]) => ({ navItemId, read: f.read, write: f.write, create: f.create, delete: f.delete, navigate: f.navigate }));
    setSavingPerms(true);
    runSave({
      save: async () => { await api.put(`/api/v1/security/roles/${selOid}/permissions`, { perms: payload }); load(); },
      successMessage: 'Permissions updated successfully.',
      onError: e => notify.error(errMsg(e)),
    }).finally(() => setSavingPerms(false));
  };

  // ── UI-8: inline (in-cell) editing of the single Name column.  Double-click
  //    the cell → input appears → Enter/blur commits via the EXISTING role PUT →
  //    useNotify; Escape reverts.  Single click keeps opening the detail
  //    (deferred 250 ms so a double-click can cancel it).  Standing rule 11:
  //    the reason gate wraps the audited PUT.
  //    §9 bulk copy (right-click Copy to All/Selected, components/gridEdit) is
  //    DEFERRED here: each cell commit is an immediate audited per-row PUT, so a
  //    bulk copy would spam N audited PUTs — the standard's ONE-audit-row-per-
  //    bulk-copy semantics need a bulk endpoint that doesn't exist yet.
  const gateReason = useReasonGate();
  const [editOid, setEditOid] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingCell, setSavingCell] = useState(false);
  const clickTimer = useRef<number | null>(null);

  const commitInlineEdit = async () => {
    if (!editOid || savingCell) return;
    const row = roles.find(r => r.oid === editOid);
    if (!row) { setEditOid(null); return; }
    const draft = editDraft.trim();
    if (!draft) { notify.warning('Role name is required.'); return; }
    if (draft === row.roleName) { setEditOid(null); return; }
    const reason = await gateReason('Roles', 'edit');
    if (reason === null) { setEditOid(null); return; }
    setSavingCell(true);
    try {
      // Same EXISTING role PUT the detail Save uses — isAdministrative/sort
      // round-trip unchanged from the list row.
      await runWithAuditReason(reason, () =>
        api.put(`/api/v1/security/roles/${row.oid}`, { roleName: draft, isAdministrative: row.isAdministrative, sort: row.sort }));
      setEditOid(null);
      notify.success('Change saved.');
      load();
    } catch (e) { notify.error(errMsg(e)); }
    finally { setSavingCell(false); }
  };

  const nameCell = (row: RoleItem) => {
    const stop = (e: React.MouseEvent) => e.stopPropagation();
    if (editOid === row.oid) {
      return (
        <span onClick={stop} onDoubleClick={stop}>
          <input autoFocus className="ai-inline-edit" value={editDraft} disabled={savingCell}
            onChange={e => setEditDraft(e.target.value)}
            onBlur={() => { void commitInlineEdit(); }}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); void commitInlineEdit(); }
              else if (e.key === 'Escape') { e.preventDefault(); setEditOid(null); }
            }} />
        </span>
      );
    }
    return (
      <span className="ai-editcell" title="Double-click to edit"
        onClick={e => {
          // Defer the row's open-detail so a double-click can cancel it.
          e.stopPropagation();
          if (clickTimer.current) window.clearTimeout(clickTimer.current);
          clickTimer.current = window.setTimeout(() => { clickTimer.current = null; void openEdit(row.oid); }, 250);
        }}
        onDoubleClick={e => {
          e.stopPropagation();
          if (clickTimer.current) { window.clearTimeout(clickTimer.current); clickTimer.current = null; }
          setEditDraft(row.roleName); setEditOid(row.oid);
        }}>
        {row.roleName}
      </span>
    );
  };

  const roleCols: AppColumn<RoleItem>[] = [
    // ALPACA-style per-row edit glyph — first data column, right after the
    // selection checkbox (AppDataGrid renders `selectable` checkboxes itself).
    { field: '__edit', header: 'Edit', width: 44, align: 'center',
      sortable: false, filterable: false,
      render: r => (
        <button type="button" className="sec-rowedit-btn" title="Edit"
          onClick={e => { e.stopPropagation(); void openEdit(r.oid); }}>
          <i className="ti ti-pencil" />
        </button>
      ) },
    { field: 'roleName', header: 'Name', render: nameCell },
  ];

  // Prev / next record arrows walk the current (search-filtered) list order.
  const idx = selOid ? shown.findIndex(r => r.oid === selOid) : -1;
  const prevOid = idx > 0 ? (shown[idx - 1]?.oid ?? null) : null;
  const nextOid = idx >= 0 && idx < shown.length - 1 ? (shown[idx + 1]?.oid ?? null) : null;

  let lastParent = '';

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the
  // bespoke Roles full-page detail.  Only ONE mod+s is ever bound so the three
  // context saves never collide — a section-specific Save (Permissions /
  // Dashboards) takes it over ONLY while that section shows; otherwise mod+s
  // Saves the role.  (Both sections are feature-flagged off today, so in the
  // shipped UI mod+s = Save the role.)  Link / Unlink (the MAC ID child grid)
  // bind on an existing role in edit mode, each mirroring its button's
  // visibility + disabled.  Built as a plain array, then passed to the ONE
  // unconditional useHotkeys call ABOVE the first early return; empty in the
  // list landing and read-only view (no editable action is shown there).
  const roleHotkeys: HotkeyDef[] = [];
  if (mode === 'new' || mode === 'edit') {
    if (SHOW_SCREEN_PERMISSIONS && mode === 'edit') {
      roleHotkeys.push({ combo: 'mod+s', label: 'Save permissions', group: 'Roles', order: 1,
        run: () => savePerms(), disabled: savingPerms });
    } else if (SHOW_DASHBOARD_GRANTS && mode === 'edit') {
      roleHotkeys.push({ combo: 'mod+s', label: 'Save dashboards', group: 'Roles', order: 1,
        run: () => saveDash(), disabled: savingDash });
    } else {
      roleHotkeys.push({ combo: 'mod+s', label: 'Save', group: 'Roles', order: 1,
        run: () => saveRole(), disabled: saving });
    }
    if (mode === 'edit') {
      roleHotkeys.push({ combo: 'alt+l', label: 'Link MAC ID', group: 'Roles', order: 2,
        run: () => setMacPickerOpen(true) });
      roleHotkeys.push({ combo: 'alt+u', label: 'Unlink MAC ID', group: 'Roles', order: 3,
        run: () => unlinkSelectedMacs(), disabled: selMacRows.size === 0 });
    }
    // Cancel closes the detail (matches the edit-mode bar's Cancel button).
    roleHotkeys.push({ combo: 'esc', label: 'Cancel', group: 'Roles', order: 4,
      allowInInput: false, disabled: saving, run: () => closeDetail() });
  } else if (mode === 'view') {
    // VIEW-mode detail bar CRUD launchers (New · Edit · Delete · Refresh · Close).
    roleHotkeys.push(
      { combo: 'alt+n', label: 'New', group: 'Roles', order: 1, run: () => openNew() },
      { combo: 'alt+e', label: 'Edit', group: 'Roles', order: 2, run: () => setMode('edit') },
      { combo: 'alt+d', label: 'Delete', group: 'Roles', order: 3, disabled: !selOid, run: () => { void deleteCurrent(); } },
      { combo: 'alt+r', label: 'Refresh', group: 'Roles', order: 4, run: () => refreshCurrent() },
      { combo: 'esc', label: 'Close', group: 'Roles', order: 5, allowInInput: false, run: () => closeDetail() },
    );
  }
  useHotkeys(roleHotkeys);

  // ═══════════════════ LIST (landing view) ═══════════════════
  if (mode === 'list') {
    return (
      <div className="sec-page">
        <div className="sec-head">
          {/* List/Detail segmented switch (BO UI Standard) — top-LEFT, same
              placement as ModuleListView. Detail opens the checkbox-selected
              row (exactly one) or the first row of the current list. */}
          <div style={{ padding: '10px 0 0' }}>
            <ViewToggle view="list" detailDisabled={shown.length === 0}
              onChange={(v) => {
                if (v !== 'detail') return;
                const id = sel.size === 1 ? String(Array.from(sel)[0]) : shown[0]?.oid;
                if (id) void openEdit(id);
              }} />
          </div>
          <FormTitle pageId="sec-roles" loadMs={loadMs} loading={loading} auditFormName="Roles" />
          <div className="sec-sub">{roles.length} item{roles.length === 1 ? '' : 's'}</div>
        </div>
        <div className="sec-lbody">
          <AppToolbar
            onNew={openNew}
            onDelete={delSelected}
            deleteDisabled={sel.size === 0}
            onRefresh={load}
            refreshing={loading}
            exportData={exportData}
          />
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input placeholder="Search roles…" value={search} onChange={e => setSearch(e.target.value)} />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
            {err && <span className="sec-error" style={{ padding: 0 }}>{err}</span>}
          </div>
          <div className="sec-gridcard">
            <AppDataGrid<RoleItem>
              rows={shown}
              columns={roleCols}
              getRowId={r => r.oid}
              loading={loading}
              selectable
              selectedIds={sel}
              onSelectionChange={setSel}
              onRowClick={r => void openEdit(r.oid)}
              emptyText="No roles found."
            />
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════ FULL-PAGE DETAIL (replaces the list) ═══════════════════
  // VIEW mode (ALPACA parity): read-only fields, Edit + Close action bar.
  const ro = mode === 'view';
  const recordName = mode === 'new' ? 'New Role' : (roleName || 'Role');
  const detailBar = (bottom: boolean) => (
    <div className={`sec-dbar${bottom ? ' bottom' : ''}`}>
      {ro ? (
        <>
          {/* Canonical AppToolbar order (CLAUDE.md §18.6): New · Edit ·
              Delete · Refresh — Audit Trail lives on the title row, not
              here (BO UI Standard v2 ruling 2026-07-01). */}
          <button className="ca-btn" onClick={openNew} title={`New (${formatCombo('alt+n')})`}>
            <span className="tb-i tb-i-green">＋</span> New
          </button>
          <button className="ca-btn" onClick={() => setMode('edit')} title={`Edit (${formatCombo('alt+e')})`}>
            <span className="tb-i tb-i-blue">✎</span> Edit
          </button>
          <button className="ca-btn" onClick={deleteCurrent} disabled={!selOid} title={`Delete (${formatCombo('alt+d')})`}>
            <span className="tb-i tb-i-red">🗑</span> Delete
          </button>
          <button className="ca-btn" onClick={refreshCurrent} title={`Refresh (${formatCombo('alt+r')})`}>
            <span className="tb-i tb-i-green">🔄</span> Refresh
          </button>
          <button className="ca-btn sec-dcancel" onClick={closeDetail} title={`Close (${formatCombo('esc')})`}>
            <span className="tb-i">↩︎</span> Close
          </button>
        </>
      ) : (
        <>
          <button className="ca-btn ca-btn-solid" onClick={saveRole} disabled={saving}
            title={`Save (${formatCombo('mod+s')})`}>
            <span className="tb-i">💾</span> Save
          </button>
          <button className="ca-btn sec-dcancel" onClick={closeDetail} disabled={saving} title={`Cancel (${formatCombo('esc')})`}>
            <span className="tb-i">↩︎</span> Cancel
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="sec-page">
      {/* List/Detail segmented switch — Detail active; List returns to the grid. */}
      <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
        <ViewToggle view="detail" onChange={(v) => { if (v === 'list') closeDetail(); }} />
      </div>
      <div className="sec-lbody sec-lbody-detail">
        <div className="sec-dwrap">
          <div className="sec-dhead">
            <div>
              <h2>
                Roles – {recordName}
                {mode !== 'new' && selOid && (
                  <AuditTrailButton variant="chip" formName="Roles" sourceId={selOid} />
                )}
              </h2>
              <div className="sec-dbc">
                <span>Home</span><i className="ti ti-chevron-right" />
                <span>User &amp; Security</span><i className="ti ti-chevron-right" />
                <a onClick={closeDetail}>Roles</a><i className="ti ti-chevron-right" />
                <span>{recordName}</span>
              </div>
            </div>
            <div className="sec-dnav">
              <button className="sec-na" title="Previous record" disabled={!prevOid || saving}
                onClick={() => prevOid && void openEdit(prevOid)}>‹</button>
              <button className="sec-na next" title="Next record" disabled={!nextOid || saving}
                onClick={() => nextOid && void openEdit(nextOid)}>›</button>
            </div>
          </div>

          {detailBar(false)}

          <div className="sec-dbody">
            <h3 className="sec-dsect">Role</h3>
            <div className="sec-dgrid2">
              <div className="sec-field">
                <label>Name <span className="sec-req">*</span></label>
                <input
                  className={`sec-input${ro ? ' readonly' : roleName.trim() ? '' : ' required-empty'}`}
                  value={roleName}
                  readOnly={ro} disabled={ro}
                  onChange={e => setRoleName(e.target.value)}
                  autoFocus={mode === 'new'}
                />
              </div>
            </div>

            {mode !== 'new' && (
              <>
                {/* ALPACA Role DetailView "MAC ID" child grid — the Module-
                    Access-Control levels assigned to this role.  VIEW mode:
                    grid stays visible, Link/Unlink hidden until Edit. */}
                <div className="sec-child">
                  <h3 className="sec-dsect">
                    MAC ID
                    <span className="tools">
                      {!ro && (
                        <>
                          <button className="ca-btn" onClick={() => setMacPickerOpen(true)}
                            title={`Link MAC ID (${formatCombo('alt+l')})`}>
                            <i className="ti ti-link" /> Link
                          </button>
                          <button className="ca-btn" onClick={unlinkSelectedMacs} disabled={selMacRows.size === 0}
                            title={`Unlink MAC ID (${formatCombo('alt+u')})`}>
                            <i className="ti ti-unlink" /> Unlink
                          </button>
                        </>
                      )}
                      <button className="ca-btn" onClick={exportMacsCsv} disabled={linkedMacs.length === 0}>
                        <span className="tb-i tb-i-teal">📤</span> Export to
                      </button>
                    </span>
                  </h3>
                  <AppDataGrid<MacRef>
                    rows={linkedMacs}
                    columns={macCols}
                    getRowId={m => m.oid}
                    selectable
                    selectedIds={selMacRows}
                    onSelectionChange={setSelMacRows}
                    emptyText="No MAC levels linked — use Link to add one."
                    defaultPageSize={10}
                   
                  />
                </div>

                {/* Intelligence per-nav-item permission grid (RoleNavigation-
                    PermissionDetails) — hidden to match legacy's Role
                    DetailView (Model.xafml removes this LayoutGroup, leaving
                    only "MAC ID" visible). UI-only: read/write plumbing is
                    untouched (SHOW_SCREEN_PERMISSIONS, 2026-07-20). */}
                {SHOW_SCREEN_PERMISSIONS && (
                <div className="sec-card sec-child">
                  <div className="sec-card-hd">
                    <i className="ti ti-list-check" /><h3>Screen permissions — {grantedCount} granted</h3>
                    <div className="right">
                      {!ro && (
                        <button className="ca-btn ca-btn-solid" onClick={savePerms} disabled={savingPerms}
                          title={`Save permissions (${formatCombo('mod+s')})`}>
                          {savingPerms ? <><LoaderSymbol size={13} /> Saving…</> : <><span className="tb-i">💾</span> Save permissions</>}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="sec-card-bd">
                    {isAdmin && <div className="sec-note">This is an administrative role — it sees every screen regardless of the grid below.</div>}
                    <div className="list-search" style={{ marginBottom: 10 }}>
                      <span className="tb-i">🔍</span>
                      <input placeholder="Filter screens by name / path / view…" value={permSearch} onChange={e => setPermSearch(e.target.value)} />
                      {permSearch && <span className="list-search-clear" onClick={() => setPermSearch('')}>×</span>}
                    </div>
                    {!ro && (
                      <div className="sec-actions" style={{ marginBottom: 10 }}>
                        <span className="sec-count">Filtered ({filteredNav.length}):</span>
                        <button className="ca-btn" onClick={() => bulkSet('navigate', true)}>+ Nav all</button>
                        <button className="ca-btn" onClick={() => bulkSet('navigate', false)}>− Nav all</button>
                        <button className="ca-btn" onClick={() => { for (const k of FLAG_KEYS) bulkSet(k, false); }}>Clear all flags</button>
                      </div>
                    )}

                    <div className="sec-pgrid-hd">
                      <div>Screen</div>
                      {FLAG_KEYS.map(k => <div key={k} className="c">{FLAG_LABEL[k]}</div>)}
                    </div>
                    <div className="sec-pgrid-scroll">
                      {filteredNav.map(n => {
                        const f = flagsOf(n.id);
                        const grp = n.parent ?? '';
                        const showGrp = grp !== lastParent;
                        lastParent = grp;
                        return (
                          <div key={n.id}>
                            {showGrp && grp && <div className="sec-pgrid-grp">{grp}</div>}
                            <div className={`sec-pgrid-row ${anyFlag(f) ? 'on' : ''}`}>
                              <div className="nm" title={n.itempath ?? ''}>{n.caption || n.navigationId || '(unnamed)'}</div>
                              {FLAG_KEYS.map(k => (
                                <div key={k} className="c"><input type="checkbox" checked={f[k]} disabled={ro} onChange={e => setFlag(n.id, k, e.target.checked)} /></div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {filteredNav.length === 0 && <div className="sec-empty">No screens match.</div>}
                    </div>
                    <div className="sec-hint">
                      <b>Nav</b> = the screen appears in the user's menu (what AI gates on). Read / Write / Create / Delete are honoured by ALPACA's security engine.
                      Permissions cascade automatically — ticking <b>Read</b> enables <b>Nav</b>; ticking <b>Write/Create/Delete</b> enables <b>Read + Nav</b>; clearing <b>Nav</b> (or <b>Read</b>) clears the levels below it.
                      Changes take effect within ~1 minute (permission cache).
                    </div>
                  </div>
                </div>
                )}

                {SHOW_DASHBOARD_GRANTS && (
                <div className="sec-card sec-child">
                  <div className="sec-card-hd">
                    <i className="ti ti-layout-dashboard" /><h3>Dashboards &amp; Backlog — {roleDash.size} granted</h3>
                    <div className="right">
                      {!ro && (
                        <button className="ca-btn ca-btn-solid" onClick={saveDash} disabled={savingDash}
                          title={`Save dashboards (${formatCombo('mod+s')})`}>
                          {savingDash ? <><LoaderSymbol size={13} /> Saving…</> : <><span className="tb-i">💾</span> Save dashboards</>}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="sec-card-bd">
                    {isAdmin && <div className="sec-note">This is an administrative role — it sees every dashboard regardless of the list below.</div>}
                    <div className="list-search" style={{ marginBottom: 10 }}>
                      <span className="tb-i">🔍</span>
                      <input placeholder="Filter dashboards…" value={dashSearch} onChange={e => setDashSearch(e.target.value)} />
                      {dashSearch && <span className="list-search-clear" onClick={() => setDashSearch('')}>×</span>}
                    </div>
                    <div className="sec-checks" style={{ maxHeight: 280, overflowY: 'auto' }}>
                      {shownDash.map(d => (
                        <label key={d.oid} className="sec-check">
                          <input type="checkbox" checked={roleDash.has(d.oid)} disabled={ro} onChange={() => toggleDash(d.oid)} /> {d.title || '(untitled)'}
                        </label>
                      ))}
                      {shownDash.length === 0 && <div className="sec-empty">No dashboards.</div>}
                    </div>
                    <div className="sec-hint">
                      Grants Sample-Backlog / dashboard views to this role via <code>AssignDashboardToUserDepartment</code> —
                      only this role's token is added or removed (other roles untouched). Takes effect within ~1 minute.
                    </div>
                  </div>
                </div>
                )}
              </>
            )}
          </div>

          {detailBar(true)}
        </div>
      </div>

      {/* Shared Link picker — all MACs not yet linked appear on the left. */}
      <DualListPickerDialog
        open={macPickerOpen}
        title="Link MAC ID"
        icon="ti-lock-cog"
        available={allMacs.map(m => ({ id: m.oid, label: m.macId, hint: m.description || undefined }))}
        selectedIds={[...roleMacs]}
        onOk={ids => { setRoleMacs(new Set(ids)); setSelMacRows(new Set()); setMacPickerOpen(false); }}
        onCancel={() => setMacPickerOpen(false)}
      />
    </div>
  );
}
